import React from 'react';

export type PropertyRowProps = {
  label: string;
  icon?: React.ReactNode;
  /** Rendered while the row is idle. */
  value: React.ReactNode;
  /** Rendered instead of `value` while `editing`. Without it the row is never clickable. */
  editor?: React.ReactNode;
  editing?: boolean;
  onStartEdit?: () => void;
  readOnly?: boolean;
  /** Lets the value column use the full row width (custom fields, the author block). */
  stacked?: boolean;
  className?: string;
};

/**
 * One line of the drawer's properties sidebar: a fixed label column and a value that turns into an
 * editor when clicked. Rows whose child component owns its own edit affordance (the assignee
 * selector, custom fields) pass `readOnly` so this row does not add a second one.
 */
const PropertyRow: React.FC<PropertyRowProps> = ({
  label,
  icon,
  value,
  editor,
  editing = false,
  onStartEdit,
  readOnly = false,
  stacked = false,
  className,
}) => {
  const interactive = !readOnly && !!editor && !!onStartEdit;

  const classes = ['task-prop'];
  if (stacked) classes.push('task-prop--stacked');
  if (editing) classes.push('is-editing');
  if (interactive) classes.push('is-interactive');
  if (className) classes.push(className);

  return (
    <div className={classes.join(' ')}>
      <div className="task-prop__label">
        {icon ? <span className="task-prop__icon">{icon}</span> : null}
        <span className="task-prop__label-text">{label}</span>
      </div>
      {editing && editor ? (
        <div className="task-prop__value task-prop__value--editing">{editor}</div>
      ) : interactive ? (
        <div
          className="task-prop__value task-prop__value--button"
          role="button"
          tabIndex={0}
          onClick={onStartEdit}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onStartEdit?.();
            }
          }}
        >
          {value}
        </div>
      ) : (
        <div className="task-prop__value">{value}</div>
      )}
    </div>
  );
};

export default PropertyRow;
